import { useEffect } from 'react';

export interface DialogButton {
  label: string;
  onClick: () => void;
  variant?: 'primary' | 'secondary' | 'danger';
}

export interface DialogProps {
  isOpen: boolean;
  title?: string;
  message: string;
  buttons?: DialogButton[];
  onClose?: () => void;
  closable?: boolean;
}

/**
 * Компонент диалогового окна в Telegram-стиле
 */
export function Dialog({
  isOpen,
  title,
  message,
  buttons = [],
  onClose,
  closable = true
}: DialogProps) {
  // Закрытие по ESC
  useEffect(() => {
    if (!isOpen || !closable) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && onClose) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, closable, onClose]);

  if (!isOpen) return null;

  const dialogButtons = buttons.length > 0 
    ? buttons 
    : [{ label: 'OK', onClick: onClose || (() => {}), variant: 'primary' as const }];

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (closable && e.target === e.currentTarget && onClose) {
      onClose();
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        animation: 'fadeIn 0.2s ease-out',
      }}
      onClick={handleOverlayClick}
    >
      <div
        style={{
          backgroundColor: 'var(--background)',
          borderRadius: '16px',
          padding: '24px',
          maxWidth: '400px',
          width: '100%',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          animation: 'slideUp 0.2s ease-out',
          border: '1px solid var(--border, rgba(255, 255, 255, 0.1))',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {title && (
          <h2
            style={{
              margin: '0 0 16px 0',
              fontSize: '20px',
              fontWeight: '600',
              color: 'var(--foreground)',
              lineHeight: '1.4',
            }}
          >
            {title}
          </h2>
        )}
        
        <p
          style={{
            margin: '0 0 24px 0',
            fontSize: '16px',
            lineHeight: '1.5',
            color: 'var(--foreground)',
            wordWrap: 'break-word',
            whiteSpace: 'pre-wrap',
          }}
        >
          {message}
        </p>

        <div
          style={{
            display: 'flex',
            gap: '12px',
            justifyContent: dialogButtons.length === 1 ? 'center' : 'flex-end',
            flexDirection: dialogButtons.length > 2 ? 'column' : 'row',
          }}
        >
          {dialogButtons.map((button, index) => (
            <button
              key={index}
              onClick={button.onClick}
              style={{
                padding: '12px 24px',
                borderRadius: '12px',
                border: 'none',
                fontSize: '16px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                minWidth: dialogButtons.length === 1 ? '120px' : 'auto',
                backgroundColor:
                  button.variant === 'primary'
                    ? 'var(--accent, #3390ec)'
                    : button.variant === 'danger'
                    ? 'var(--destructive, #e53935)'
                    : 'var(--muted, rgba(255, 255, 255, 0.1))',
                color:
                  button.variant === 'primary' || button.variant === 'danger'
                    ? 'var(--accent-foreground, #ffffff)'
                    : 'var(--foreground)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.opacity = '0.9';
                e.currentTarget.style.transform = 'scale(0.98)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = '1';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              {button.label}
            </button>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}
